import React, { useState } from "react";
import { Badge, Box, Button, ButtonGroup, Flex, Text } from "@chakra-ui/react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip as ChartTooltip, Legend,
} from "chart.js";
import { num } from "../SaudeAnunciosML/comum";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ChartTooltip, Legend);

export const TINTA = "#4A5568";
export const GRADE = "#e1e0d9";
export const COR_PREMIUM = "#6B46C1";
export const COR_CLASSICO = "#2a78d6";
export const COR_REFERENCIA = "#718096";
export const TAMANHO_PEQUENO = { base: "md", lg: "xs" };
export const TAMANHO_CAMPO = { base: "md", lg: "sm" };

export const CONTAS = [
  { id: 1, nome: "Conta 1" },
  { id: 2, nome: "Conta 2" },
  { id: 3, nome: "Conta 3" },
];

export const TIPOS = {
  gold_pro: { label: "Premium", cor: "purple" },
  gold_special: { label: "Clássico", cor: "blue" },
};

export const brl = (v, casas = 2) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "—";
  return `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}`;
};

export const pctFracao = (v, casas = 1) => {
  if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "—";
  const n = Number(v) * 100;
  return `${n > 0 ? "+" : ""}${num(n, casas)}%`;
};

export const conversao = (pedidos, visitas) => {
  const v = Number(visitas || 0);
  return v > 0 ? `${num((Number(pedidos || 0) / v) * 100, 2)}%` : "—";
};

export function BadgeTipo({ tipo }) {
  if (!tipo) return null;
  const t = TIPOS[tipo];
  return (
    <Badge colorScheme={t ? t.cor : "gray"} fontSize="0.7em" whiteSpace="nowrap">
      {t ? t.label : tipo}
    </Badge>
  );
}

export function Cartao({ titulo, extra, children, ...rest }) {
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="gray.100" p={{ base: 3, md: 4 }} minW={0} {...rest}>
      {(titulo || extra) && (
        <Flex justify="space-between" align="center" gap={2} mb={3} flexWrap="wrap">
          {titulo && <Text as="h2" fontWeight="bold" fontSize="sm" color="gray.800">{titulo}</Text>}
          {extra}
        </Flex>
      )}
      {children}
    </Box>
  );
}

export function CartaoGrafico({ titulo, subtitulo, grafico, tabela, altura = { base: "220px", md: "280px" } }) {
  const [verTabela, setVerTabela] = useState(false);
  return (
    <Cartao
      titulo={titulo}
      extra={(
        <Button size={TAMANHO_PEQUENO} variant="outline" onClick={() => setVerTabela((v) => !v)}>
          {verTabela ? "Ver gráfico" : "Ver tabela"}
        </Button>
      )}
    >
      {subtitulo && <Text fontSize="xs" color="gray.600" mt={-2} mb={2}>{subtitulo}</Text>}
      {verTabela ? (
        <Box overflowX="auto" maxH="320px" overflowY="auto" borderWidth="1px" borderColor="gray.100" borderRadius="md">
          {tabela}
        </Box>
      ) : (
        <Box h={altura}>{grafico}</Box>
      )}
    </Cartao>
  );
}

export function SeletorDias({ valor, onChange, opcoes = [7, 30, 90], rotulo = "Período" }) {
  return (
    <ButtonGroup size={TAMANHO_PEQUENO} isAttached variant="outline" role="group" aria-label={rotulo}>
      {opcoes.map((d) => (
        <Button key={d} onClick={() => onChange(d)} isActive={valor === d} aria-pressed={valor === d}
          bg={valor === d ? "orange.50" : "white"} borderColor={valor === d ? "orange.300" : "gray.200"}>
          {d} dias
        </Button>
      ))}
    </ButtonGroup>
  );
}

export const opcoesLinha = (formatar = (v) => num(v)) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { position: "bottom", labels: { boxWidth: 12, boxHeight: 2, color: TINTA, font: { size: 11 } } },
    tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${formatar(ctx.parsed.y)}` } },
  },
  scales: {
    x: { grid: { display: false }, border: { color: GRADE }, ticks: { color: TINTA, maxRotation: 0, autoSkip: true, maxTicksLimit: 10, font: { size: 10 } } },
    y: { beginAtZero: true, grace: "10%", grid: { color: GRADE }, border: { display: false }, ticks: { color: TINTA, font: { size: 10 }, callback: (v) => formatar(v) } },
  },
});

export const serieLinha = (label, dados, cor, extra = {}) => ({
  label,
  data: dados,
  borderColor: cor,
  backgroundColor: cor,
  borderWidth: 2,
  tension: 0.25,
  pointRadius: dados.length > 14 ? 0 : 3,
  pointHoverRadius: 5,
  pointBorderColor: "#ffffff",
  pointBorderWidth: 2,
  spanGaps: true,
  ...extra,
});

export const linhaVerticalEm = (indice, rotulo) => ({
  id: `linha-vertical-${indice}`,
  afterDatasetsDraw(chart) {
    if (indice === null || indice === undefined || indice < 0) return;
    const x = chart.scales.x.getPixelForValue(indice);
    const { top, bottom } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = "#DD6B20";
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#C05621";
    ctx.font = "11px sans-serif";
    ctx.fillText(rotulo, Math.min(x + 4, chart.chartArea.right - 60), top + 12);
    ctx.restore();
  },
});
